/**
 * Authority API Service
 * 
 * Handles all HTTP requests related to authority management
 */

import { 
  Authority, 
  AuthorityFormData, 
  AuthorityQueryParams, 
  ApiResponse,
  AuthorityAudit 
} from '../types/authority';

import apiClient from './apiService';

class AuthorityApiService {
  // Using session cookies; headers set by apiClient
  // Backend routes for authorities are under the org module prefix
  private readonly basePath = '/api/v1/org/authorities';

  async getAuthorities(params?: AuthorityQueryParams): Promise<ApiResponse<Authority[]>> {
    const queryString = params ? new URLSearchParams(
      Object.entries(params).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>)
    ).toString() : '';

    const { data } = await apiClient.get<ApiResponse<Authority[]>>(
      `${this.basePath}${queryString ? `?${queryString}` : ''}`
    );
    return data;
  }

  // Approved + active authorities only (entity-scoped, unbounded, sorted by name).
  // Use for form dropdowns instead of getAuthorities() which returns all states.
  async getAuthoritiesDropdown(): Promise<{ total: number; eligible: number; items: { id: number; name: string; code?: string }[] }> {
    const { data } = await apiClient.get<{ total: number; eligible: number; items: { id: number; name: string; code?: string }[] }>(
      `${this.basePath}/dropdown`
    );
    return data;
  }

  async getAuthority(id: number): Promise<ApiResponse<Authority>> {
    const { data } = await apiClient.get<ApiResponse<Authority>>(`${this.basePath}/${id}`);
    return data;
  }

  async createAuthority(data: AuthorityFormData): Promise<ApiResponse<Authority>> {
    const res = await apiClient.post<ApiResponse<Authority>>(`${this.basePath}`, data);
    return res.data;
  }

  async updateAuthority(id: number, data: AuthorityFormData): Promise<ApiResponse<Authority>> {
    const res = await apiClient.put<ApiResponse<Authority>>(`${this.basePath}/${id}`, data);
    return res.data;
  }

  async deleteAuthority(id: number, reason?: string): Promise<ApiResponse<void>> {
    const res = await apiClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`, {
      params: { reason: reason || '' }
    });
    return res.data;
  }

  async approveAuthority(id: number, comments?: string): Promise<ApiResponse<Authority>> {
    const res = await apiClient.post<ApiResponse<Authority>>(`${this.basePath}/${id}/approve`, { comments });
    return res.data;
  }

  async rejectAuthority(id: number, reason: string): Promise<ApiResponse<Authority>> {
    const res = await apiClient.post<ApiResponse<Authority>>(`${this.basePath}/${id}/reject`, { reason });
    return res.data;
  }

  async getAuthorityAudit(id: number): Promise<ApiResponse<AuthorityAudit[]>> {
    const { data } = await apiClient.get<ApiResponse<AuthorityAudit[]>>(`${this.basePath}/${id}/audit`);
    return data;
  }

  async getPendingApprovals(): Promise<ApiResponse<Authority[]>> {
    const { data } = await apiClient.get<ApiResponse<Authority[]>>(`${this.basePath}/pending-approvals`);
    return data;
  }

  async getAuthorityPath(id: number): Promise<ApiResponse<any[]>> {
    const { data } = await apiClient.get<ApiResponse<any[]>>(`${this.basePath}/${id}/path`);
    return data;
  }

}

export const authorityApi = new AuthorityApiService();